class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    // Phase 1.1 / B-01 D-B01-4: subscribe to product-form.js pubsub cart-error
    // (published at assets/product-form.js:49). We do NOT extend product-form.js.
    this.cartErrorUnsubscribe = null;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartError) {
      this.cartErrorUnsubscribe = subscribe(PUB_SUB_EVENTS.cartError, (payload) => {
        this.renderError(payload);
      });
    }

    // Phase 1.1 / B-01 D-B01-3: 5 s auto-dismiss with hover/focus pause.
    this.DISMISS_MS = 5000;
    this.dismissTimer = null;
    this.addEventListener('pointerenter', () => this.pauseDismiss());
    this.addEventListener('pointerleave', () => this.resumeDismiss());
    this.addEventListener('focusin', () => this.pauseDismiss());
    this.addEventListener('focusout', (evt) => {
      if (!this.contains(evt.relatedTarget)) this.resumeDismiss();
    });
  }

  disconnectedCallback() {
    if (this.cartErrorUnsubscribe) this.cartErrorUnsubscribe();
    this.clearDismiss();
  }

  open() {
    this.notification.classList.add('animate', 'active');

    // Phase 1.1 / A11y per COMPONENT-SPECS §3: NOT focus-trapping.
    // trapFocus(this.notification) was removed; ESC dismiss still works via line-9 listener.
    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
    this.startDismiss();
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    this.clearDismiss();
    // Clear error state so next success add renders correctly.
    this.notification.classList.remove('err', 'first');
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    // Clear error state before the success render so consecutive
    // success-after-error works (Task 2 acceptance criteria).
    this.notification.classList.remove('err');
    this.notification.setAttribute('role', 'dialog');
    this.notification.setAttribute('aria-live', 'polite');
    // Remove any existing .err-msg block from a prior error render.
    const staleErr = this.notification.querySelector('.err-msg');
    if (staleErr) staleErr.remove();

    this.getSectionsToRender().forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) {
        el.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      }
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  renderError(payload) {
    // Error state toast: replaces product block with .err-msg, hides CTAs.
    this.notification.classList.remove('first');
    this.notification.classList.add('err');
    // Swap role for assertive announcement per COMPONENT-SPECS §3 A11y.
    this.notification.setAttribute('role', 'alert');
    this.notification.setAttribute('aria-live', 'assertive');

    let msgEl = this.notification.querySelector('.err-msg');
    if (!msgEl) {
      msgEl = document.createElement('div');
      msgEl.className = 'err-msg';
      // Insert right after header, before product/cta blocks (which are hidden via .err).
      const header = this.notification.querySelector('.cart-notification__header');
      if (header && header.parentNode) {
        header.parentNode.insertBefore(msgEl, header.nextSibling);
      } else {
        this.notification.appendChild(msgEl);
      }
    }

    // White-label: phone + all visible strings come from the host element's
    // data attributes, populated by Liquid (snippets/cart-notification.liquid)
    // from shop.phone + general.cart.error_* locale keys. Nothing in this
    // file is shop- or language-specific.
    const phoneDisplay = (this.dataset.fallbackPhoneDisplay || '').trim();
    const phoneTel = (this.dataset.fallbackPhoneTel || '').trim();
    const msgPrefix = this.dataset.msgErrorPrefix || '';
    const msgSuffix = this.dataset.msgErrorSuffix || '';
    const msgNoPhone = this.dataset.msgErrorNoPhone || '';
    let fallback;
    if (phoneDisplay && phoneTel && msgPrefix) {
      // Safe DOM build — never innerHTML on author-controlled strings.
      fallback = document.createElement('span');
      fallback.appendChild(document.createTextNode(msgPrefix));
      const a = document.createElement('a');
      a.href = `tel:${phoneTel}`;
      a.textContent = phoneDisplay;
      fallback.appendChild(a);
      fallback.appendChild(document.createTextNode(msgSuffix));
    } else {
      fallback = document.createTextNode(msgNoPhone);
    }
    const providedMessage = payload && (payload.message || payload.errors);
    msgEl.replaceChildren();
    if (providedMessage && typeof providedMessage === 'string') {
      const p = document.createElement('p');
      p.style.margin = '0 0 8px 0';
      p.textContent = providedMessage;
      msgEl.appendChild(p);
    }
    const p2 = document.createElement('p');
    p2.style.margin = '0';
    p2.appendChild(fallback);
    msgEl.appendChild(p2);

    this.open();
  }

  startDismiss() {
    this.clearDismiss();
    this.dismissTimer = setTimeout(() => this.close(), this.DISMISS_MS);
  }
  pauseDismiss() { this.clearDismiss(); }
  resumeDismiss() { this.startDismiss(); }
  clearDismiss() {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const match = doc.querySelector(selector);
    return match ? match.innerHTML : '';
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
